import { config } from '../config/environment';

// API Response types
export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
  code?: string;
}

export interface ApiError {
  message: string;
  code?: string;
  details?: any;
}

// HTTP Methods
type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';

// Request options
interface RequestOptions {
  method: HttpMethod;
  headers?: Record<string, string>;
  body?: any;
  timeout?: number;
  /** When true, a 401 response throws normally instead of redirecting to /. Use for login endpoints. */
  skipAuthRedirect?: boolean;
}

// Base API Service Class
class ApiService {
  private baseUrl: string;
  private defaultHeaders: Record<string, string>;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
    this.defaultHeaders = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    };
  }

  // Generic request method
  private async request<T>(
    endpoint: string,
    options: RequestOptions
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseUrl}${endpoint}`;
    const controller = new AbortController();
    const token = localStorage.getItem('authToken');
    if (token) {
      this.defaultHeaders['Authorization'] = `Bearer ${token}`;
    }
    
    // Set timeout
    const timeoutId = setTimeout(() => controller.abort(), options.timeout || 30000);

    try {
      // Debug: Log outgoing request details
      console.log('API Request:', {
        method: options.method,
        url,
        headers: {
          ...this.defaultHeaders,
          ...options.headers,
        },
        body: options.body ? JSON.stringify(options.body) : undefined,
      });

      const response = await fetch(url, {
        method: options.method,
        headers: {
          ...this.defaultHeaders,
          ...options.headers,
        },
        body: options.body ? JSON.stringify(options.body) : undefined,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      // Log the full response for debugging
      console.log('Full API Response:', {
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries()),
        url: response.url
      });

      if (!response.ok) {
        if (response.status === 401 && !options.skipAuthRedirect) {
          localStorage.removeItem('authToken');
          localStorage.removeItem('userData');
          localStorage.removeItem('userType');
          window.location.href = '/';
          throw new Error('Session expired. Please log in again.');
        }
        const errorData = await response.json().catch(() => ({}));
        console.error('API Error Response:', {
          status: response.status,
          statusText: response.statusText,
          errorData: errorData,
          fullError: errorData
        });
        throw new Error(errorData.message || errorData.detail || `HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('API Success Response:', data);
      return {
        success: true,
        data,
      };
    } catch (error) {
      clearTimeout(timeoutId);
      
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          return {
            success: false,
            error: 'Request timeout',
          };
        }
        return {
          success: false,
          error: error.message,
        };
      }
      
      return {
        success: false,
        error: 'Unknown error occurred',
      };
    }
  }

  // HTTP Methods
  async get<T>(endpoint: string, headers?: Record<string, string>): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'GET', headers });
  }

  async post<T>(endpoint: string, data?: any, headers?: Record<string, string>, skipAuthRedirect?: boolean): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'POST', body: data, headers, skipAuthRedirect });
  }

  async put<T>(endpoint: string, data?: any, headers?: Record<string, string>): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'PUT', body: data, headers });
  }

  async delete<T>(endpoint: string, headers?: Record<string, string>): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'DELETE', headers });
  }

  async patch<T>(endpoint: string, data?: any, headers?: Record<string, string>): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'PATCH', body: data, headers });
  }
}

// Service instances for different endpoints
export const mainApiService = new ApiService(config.apiBaseUrl);

// Utility functions for common API patterns
export const apiUtils = {
  // Handle API responses with error checking
  handleResponse: <T>(response: ApiResponse<T>): T => {
    if (!response.success) {
      throw new Error(response.error || 'API request failed');
    }
    return response.data as T;
  },

  // Create error response
  createErrorResponse: (error: string, code?: string): ApiResponse => ({
    success: false,
    error,
    code,
  }),

  // Create success response
  createSuccessResponse: <T>(data: T, message?: string): ApiResponse<T> => ({
    success: true,
    data,
    message,
  }),

  // Add authentication headers
  addAuthHeaders: (token: string): Record<string, string> => ({
    'Authorization': `Bearer ${token}`,
  }),

  // Add API key headers
  addApiKeyHeaders: (apiKey: string): Record<string, string> => ({
    'X-API-Key': apiKey,
  }),
};

export default mainApiService;
